import { useCallback } from 'react';

import {
  tasksKnowledgeDraftsCreate,
  tasksPublishCreate,
  tasksSummarizeCreate,
} from '~/data/generated/endpoints/tasks/tasks';
import type { TaskPublishEntryRequest } from '~/data/generated/model';
import { useRevalidateTasks } from '~/data/hooks/taskKeys';
import { useRevalidateKnowledge } from '~/data/hooks/useKnowledge';

export function useTaskKnowledge(taskId: string) {
  const revalidateTasks = useRevalidateTasks();
  const revalidateKnowledge = useRevalidateKnowledge();

  const summarize = useCallback(async () => {
    const { data } = await tasksSummarizeCreate(taskId);
    await revalidateTasks();
    return data;
  }, [revalidateTasks, taskId]);

  const draftKnowledge = useCallback(
    async (signal?: AbortSignal) =>
      (await tasksKnowledgeDraftsCreate(taskId, { signal })).data.drafts,
    [taskId],
  );

  const publish = useCallback(
    async (entries: TaskPublishEntryRequest[]) => {
      const { data } = await tasksPublishCreate(taskId, { entries });
      await Promise.all([revalidateTasks(), revalidateKnowledge()]);
      return data;
    },
    [revalidateKnowledge, revalidateTasks, taskId],
  );

  return { summarize, draftKnowledge, publish };
}
